import {Extension, type AnyExtension} from "@tiptap/core";
import Document from "@tiptap/extension-document";
import TextAlign from "@tiptap/extension-text-align";
import {Placeholder} from "@tiptap/extensions";
import StarterKit from "@tiptap/starter-kit";
import {Markdown} from "tiptap-markdown";
import type {VariableCatalogue} from "../../newVariablePicker";
import {variableExtensions} from "../../newVariablePicker/editor";
import EmojiExtension from "./emoji/EmojiExtension";
import {MARKDOWN_OPTIONS, MarkdownParagraph} from "./markdown";

const SingleLineDocument = Document.extend({content: 'paragraph'});

/*
    Enter does nothing on one line. Lowest priority, so an open `/` or `:` list still takes Enter
    first; and handled, so the browser does not put its own line break into the text.
 */
const SingleLineEnter = Extension.create({
    name: 'singleLineEnter',
    priority: 0,
    addKeyboardShortcuts() {
        return {
            'Enter': () => true,
            'Shift-Enter': () => true,
            'Mod-Enter': () => true,
        };
    },
});

const LINK = {openOnClick: false, autolink: false, defaultProtocol: 'https'} as const;

export interface RichTextExtensionOptions {
    singleLine: boolean;
    alignment: boolean;
    emoji: boolean;
    /** The first catalogue only; later ones are swapped in with `setVariableCatalogue`. */
    variables?: VariableCatalogue;
    placeholder: () => string;
}

/**
 * The document is what the toolbar offers: bold, italic, underline and links, lists on multi-line
 * fields, plus what the host switches on. No headings, quotes, code or strike-through, so typing
 * markdown never makes a format the toolbar cannot show or undo.
 */
export const richTextExtensions = ({
    singleLine,
    alignment,
    emoji,
    variables,
    placeholder,
}: RichTextExtensionOptions): AnyExtension[] => [
    StarterKit.configure({
        document: false,
        paragraph: false,
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        strike: false,
        horizontalRule: false,
        link: LINK,
        ...(singleLine ? {
            bulletList: false,
            orderedList: false,
            listItem: false,
            listKeymap: false,
            hardBreak: false,
            trailingNode: false,
        } as const : {}),
    }),
    ...(singleLine ? [SingleLineDocument, SingleLineEnter] : [Document]),
    MarkdownParagraph,
    Placeholder.configure({placeholder}),
    ...(alignment ? [TextAlign.configure({types: ['paragraph']})] : []),
    ...(emoji ? [EmojiExtension] : []),
    ...(variables ? variableExtensions(variables, {search: false}) : []),
    Markdown.configure(MARKDOWN_OPTIONS),
];
